"use client";

import { bisector } from "d3-array";
import { useCallback, useContext, useRef, useState } from "react";
import { ChartContext } from "../../../context/ChartContext";
import { type BaseDataPoint, defaultMargin, resolveBaselineValue } from "../../../utils/charts";
import { type EventMarkerType, getEventColor } from "../../../utils/eventColors";

const bisectDate = bisector<BaseDataPoint, Date>((d) => d.xValue).left;

/**
 * The series point nearest an instant, with its index.
 *
 * The index is carried out rather than discarded because a banded series
 * resolves its lower bound per point — `resolveBaselineValue` needs it, and
 * without it the tooltip reports a 241-246 band as the single value 246.
 */
function nearestPoint(data: BaseDataPoint[], at: Date): { point: BaseDataPoint; index: number } | undefined {
  if (data.length === 0) return undefined;

  const index = bisectDate(data, at);
  const before = data[index - 1];
  const after = data[index];
  if (!before) return after ? { point: after, index } : undefined;
  if (!after) return { point: before, index: index - 1 };

  return Math.abs(at.getTime() - before.xValue.getTime()) < Math.abs(at.getTime() - after.xValue.getTime())
    ? { point: before, index: index - 1 }
    : { point: after, index };
}

/**
 * A time span to render as a translucent highlight band on a chart.
 */
export interface EventSpan {
  /** Start of the span */
  start: Date;
  /** End of the span */
  end: Date;
  /** Human-readable label shown in tooltip */
  label: string;
  /** Optional type for color coding */
  type?: EventMarkerType;
  /** Optional per-span color override; takes precedence over the component-level color prop */
  color?: string;
  /** Optional metadata to display in tooltip */
  metadata?: Record<string, string | number>;
}

export interface ChartEventSpansProps {
  /**
   * Array of event spans to render as translucent bands.
   */
  spans: EventSpan[];
  /**
   * Override fill opacity (default 0.25). Hover state doubles this up to a cap.
   */
  opacity?: number;
  /**
   * Override fill color for all spans (defaults to getEventColor(span.type)).
   */
  color?: string;
}

/**
 * ChartEventSpans
 *
 * Renders translucent rectangular bands representing time spans (e.g., scheduled command windows)
 * on a chart. Must be used as a child of ChartContainer to access chart context.
 *
 * Renders BEFORE ChartEventMarkers so the vertical marker lines appear on top of the span bands.
 *
 * @example
 * ```tsx
 * <ChartContainer title="Device power">
 *   <LineSeries data={data} label="Power" />
 *   <ChartEventSpans
 *     spans={[
 *       { start: new Date('2024-01-01T12:00:00Z'), end: new Date('2024-01-01T14:00:00Z'), label: 'Scheduled window', type: 'command' }
 *     ]}
 *   />
 *   <ChartEventMarkers events={markers} />
 * </ChartContainer>
 * ```
 */
export function ChartEventSpans({ spans, opacity = 0.25, color }: ChartEventSpansProps) {
  const context = useContext(ChartContext);
  const [hoveredSpan, setHoveredSpan] = useState<number | null>(null);
  // `-Infinity`, not 0: `timeStamp` is milliseconds since page load, so a first
  // move inside the opening 16ms would be throttled away and the band would
  // render its highlight with no tooltip behind it.
  const lastMoveRef = useRef(Number.NEGATIVE_INFINITY);

  // Extract context values safely for use in hooks (hooks must be called unconditionally)
  const xScaleForHooks = context?.xScale;
  const yScaleForHooks = context?.yScale;
  const heightForHooks = context?.height;
  const tooltipForHooks = context?.tooltip;
  const datasets = context?.datasets;
  const seriesMetadata = context?.seriesMetadata;
  const seriesTypes = context?.seriesTypes;
  const categoryColors = context?.categoryColors;

  const [domainMin, domainMax] = (xScaleForHooks?.domain() ?? [new Date(0), new Date(0)]) as [Date, Date];
  const [rangeMin, rangeMax] = (xScaleForHooks?.range() ?? [0, 0]) as [number, number];

  /**
   * A span band is a full-height AREA, not a marker line, and it renders above
   * `ChartContainer`'s interactive overlay. So for every pixel inside one, this
   * component owns the pointer — and when it reported only the band's own label,
   * the series tooltip was dead across the whole span. On a chart whose bands
   * mark the event window, that is precisely the stretch the reader came to
   * read: the curve was visible and its values were not.
   *
   * So the band reports what a marker already reports — the series values at the
   * cursor, with the band named alongside them — rather than suppressing them.
   * `ChartEventMarkers` builds the same payload the same way; the trailing
   * `type: "event"` entry is the contract `ChartTooltip` reads to split data rows
   * from event rows.
   *
   * `mousemove`, not `mouseenter`: the value has to follow the cursor across the
   * band, and an enter-only handler would freeze it at the edge.
   */
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>, span: EventSpan, index: number) => {
      if (!xScaleForHooks || !tooltipForHooks) return;
      setHoveredSpan(index);

      // Same 16ms throttle the container's own handler uses.
      if (event.timeStamp - lastMoveRef.current < 16) return;
      lastMoveRef.current = event.timeStamp;

      const { left } = event.currentTarget.ownerSVGElement?.getBoundingClientRect() ?? { left: 0 };
      const cursorX = event.clientX - left;
      const cursorAt = xScaleForHooks.invert(cursorX);

      const seriesData: NonNullable<Parameters<typeof tooltipForHooks.show>[0]["series"]> = [];
      if (datasets && seriesMetadata) {
        datasets.forEach((dataset, datasetIndex) => {
          const metadata = seriesMetadata[datasetIndex];
          const nearest = nearestPoint(dataset, cursorAt);
          if (!nearest || !metadata) return;

          const { point, index } = nearest;
          const baselineValue = resolveBaselineValue(metadata, point, index);

          /*
           * The same row `ChartContainer` builds for its own hover, field for
           * field. Every one of these is carried by a point rather than by the
           * series, so dropping any of them makes a span's tooltip quietly
           * disagree with the one three pixels outside the band:
           *
           * - `chartY` is where a STACKED series is actually drawn, which is not
           *   its value. Positioning on the value puts the tooltip away from the
           *   series it describes, and off the plot entirely for a tall stack.
           * - `showMarker` is read as `!== false`, so omitting it draws a focus
           *   marker on a point that asked for none.
           * - `extraTooltipRows` are per-point, so they simply vanish inside a
           *   band if not passed through.
           */
          seriesData.push({
            label: metadata.label,
            value: point.yValue,
            ...(baselineValue !== undefined ? { baselineValue } : {}),
            chartY: point.chartYValue ?? point.yValue,
            showMarker: point.showTooltipMarker ?? true,
            color: metadata.color,
            type: seriesTypes?.[datasetIndex] ?? "line",
            ...(point.category ? { category: point.category, categoryColor: categoryColors?.[point.category] } : {}),
            ...(point.extraTooltipRows && point.extraTooltipRows.length > 0
              ? { extraTooltipRows: point.extraTooltipRows }
              : {}),
          });
        });
      }

      const shouldShowLeft = cursorX > (rangeMax - rangeMin) / 2 + rangeMin;
      // Use strict < / > so back-to-back spans that only touch at the boundary
      // (e.g. charge.end === idle.start) are NOT treated as overlapping, otherwise
      // the neighbor's metadata bleeds into the hovered span's tooltip.
      const overlappingSpans = spans.filter(
        (candidate) => candidate.end > candidate.start && candidate.start < span.end && candidate.end > span.start
      );
      const eventItems = overlappingSpans.map((candidate) => ({
        label: candidate.label,
        color: candidate.color ?? color ?? getEventColor(candidate.type),
        metadata: candidate.metadata,
      }));

      // The event row goes last: `ChartTooltip` renders `series.slice(0, -1)` as
      // data rows and treats the final entry as the event.
      seriesData.push({
        label: eventItems.length > 1 ? `${eventItems.length} scheduled commands` : span.label,
        value: 0,
        color: span.color ?? color ?? getEventColor(span.type),
        type: "event" as const,
      });

      // The DRAWN coordinate, not the reported value — see `chartY` above.
      const firstChartY = seriesData.length > 1 ? (seriesData[0]?.chartY ?? seriesData[0]?.value) : undefined;

      tooltipForHooks.show({
        // The cursor's instant, not the band's start: the header labels the
        // values beside it, and a band-start header on a mid-band reading would
        // date every value to the top of the window.
        xValue: cursorAt,
        series: seriesData,
        x: cursorX,
        y: typeof firstChartY === "number" && yScaleForHooks ? yScaleForHooks(firstChartY) : (heightForHooks ?? 0) / 2,
        position: shouldShowLeft ? "left" : "right",
        isEvent: true,
        eventMetadata: span.metadata,
        eventItems,
      });
    },
    [
      xScaleForHooks,
      yScaleForHooks,
      heightForHooks,
      tooltipForHooks,
      datasets,
      seriesMetadata,
      seriesTypes,
      categoryColors,
      color,
      rangeMin,
      rangeMax,
      spans,
    ]
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredSpan(null);
    tooltipForHooks?.hide();
  }, [tooltipForHooks]);

  if (!context) {
    console.warn("ChartEventSpans must be used within a ChartContainer");
    return null;
  }

  // Context is guaranteed non-null below this point
  const { xScale, height } = context;

  const chartTop = defaultMargin.top;
  const chartBottom = height - defaultMargin.bottom;
  const chartHeight = chartBottom - chartTop;

  // Filter out degenerate spans
  const validSpans = spans.filter((span) => span.end > span.start);

  if (validSpans.length === 0) {
    return null;
  }

  return (
    <g className="chart-event-spans" data-testid="chart-event-spans">
      {validSpans.map((span, index) => {
        // Clip span to visible domain
        const clampedStart = span.start < domainMin ? domainMin : span.start;
        const clampedEnd = span.end > domainMax ? domainMax : span.end;

        // Skip spans that don't overlap the visible domain at all
        if (clampedStart >= domainMax || clampedEnd <= domainMin) {
          return null;
        }

        const x1 = xScale(clampedStart);
        const x2 = xScale(clampedEnd);
        const rectWidth = x2 - x1;

        if (rectWidth <= 0) return null;

        const fillColor = span.color ?? color ?? getEventColor(span.type);
        const isHovered = hoveredSpan === index;

        return (
          <rect
            key={`${span.start.getTime()}-${span.end.getTime()}-${index}`}
            x={x1}
            y={chartTop}
            width={rectWidth}
            height={chartHeight}
            fill={fillColor}
            fillOpacity={isHovered ? Math.min((opacity ?? 0.25) * 1.8, 1) : (opacity ?? 0.25)}
            pointerEvents="all"
            style={{ cursor: "pointer" }}
            onMouseMove={(event) => handleMouseMove(event, span, index)}
            onMouseLeave={handleMouseLeave}
          />
        );
      })}
    </g>
  );
}
